import { RevealButton } from '@affine/component';
import {
  NoPermissionOrNotFound,
  NotFoundPage,
} from '@affine/component/not-found-page';
import { useSignOut } from '@affine/core/components/hooks/affine/use-sign-out';
import { FrameworkScope, useLiveData, useService } from '@toeverything/infra';
import type { ReactElement } from 'react';
import { useCallback, useMemo } from 'react';

import {
  RouteLogic,
  useNavigateHelper,
} from '../../../components/hooks/use-navigate-helper';
import { ServersService } from '../../../modules/cloud';
import { useDesktopAppLayoutReady } from '../../bootstrap/use-desktop-app-layout-ready';
import { SignIn } from '../auth/sign-in';

/**
 * only for web, should not be used in electron
 */
export const PageNotFound = ({
  noPermission,
}: {
  noPermission?: boolean;
}): ReactElement => {
  const serversService = useService(ServersService);
  const serversWithAccount = useLiveData(serversService.serversWithAccount$);

  // Check all servers for any logged in accounts to avoid showing sign-in page if user has an active session on any server
  const firstLogged = serversWithAccount.find(
    ({ account }) => account !== null
  );
  const { jumpToIndex } = useNavigateHelper();
  const openSignOutModal = useSignOut();

  const handleBackButtonClick = useCallback(
    () => jumpToIndex(RouteLogic.REPLACE),
    [jumpToIndex]
  );

  useDesktopAppLayoutReady();

  const renderBackButton = useCallback(
    (label: string, onClick: () => void) => (
      <RevealButton label={label} onClick={onClick} />
    ),
    []
  );

  // Map AuthAccountInfo to the User shape expected by NotFoundPageProps
  const user = useMemo(() => {
    const account = firstLogged?.account;
    if (!account) return undefined;
    return {
      id: account.id,
      label: account.label,
      email: account.email,
      avatar: account.avatar,
      image: account.info?.avatarUrl ?? null,
    };
  }, [firstLogged?.account]);

  // not using workbench location or router location deliberately
  // strip the origin
  const currentUrl = window.location.href.replace(window.location.origin, '');

  return (
    <FrameworkScope scope={firstLogged?.server.scope}>
      {noPermission ? (
        <NoPermissionOrNotFound
          user={user}
          onBack={handleBackButtonClick}
          onSignOut={openSignOutModal}
          signInComponent={<SignIn redirectUrl={currentUrl} />}
          renderBackButton={renderBackButton}
        />
      ) : (
        <NotFoundPage
          user={user}
          onBack={handleBackButtonClick}
          onSignOut={openSignOutModal}
          renderBackButton={renderBackButton}
        />
      )}
    </FrameworkScope>
  );
};

export const Component = () => {
  return <PageNotFound />;
};
